import type { DaySummary } from "../lib/api";
import { calorieStatus, proteinLevel } from "../lib/goals";
import { addMonths, DOW_LABELS, formatDateKey, getMonthGrid } from "../lib/monthGrid";
import { Spinner } from "./Spinner";

type Props = {
  days: DaySummary[];
  month: Date;
  onMonthChange: (d: Date) => void;
  minMonth: Date;
  onSelectDay: (key: string) => void;
  selectedDay: string;
  goal: { calories: number; protein_g: number };
  loading?: boolean;
};

export function MonthCalendar({
  days,
  month,
  onMonthChange,
  minMonth,
  onSelectDay,
  selectedDay,
  goal,
  loading,
}: Props) {
  const map = new Map(days.map((d) => [d.day, d]));
  const { year, mo, daysInMonth, firstDow, todayKey, atMin, atMax, monthLabel } = getMonthGrid(
    month,
    minMonth,
  );

  return (
    <div className="month-cal">
      <div className="heatmap-nav">
        <div className="heatmap-nav__left">
          <button
            className="ghost"
            onClick={() => onMonthChange(addMonths(month, -1))}
            disabled={atMin}
            aria-label="Previous month"
          >
            ‹
          </button>
          <span className="heatmap-nav__month">{monthLabel}</span>
          <button
            className="ghost"
            onClick={() => onMonthChange(addMonths(month, 1))}
            disabled={atMax}
            aria-label="Next month"
          >
            ›
          </button>
        </div>
        <div className="heatmap-nav__status">{loading && <Spinner size="sm" />}</div>
      </div>
      <div className="heatmap-dow-row">
        {DOW_LABELS.map((l, i) => (
          <div key={i} className="heatmap-dow">
            {l}
          </div>
        ))}
      </div>
      <div className={`month-cal-grid${loading ? " month-cal-grid--loading" : ""}`}>
        {Array.from({ length: firstDow }).map((_, i) => (
          <div key={`f${i}`} className="month-cal-cell month-cal-cell--filler" />
        ))}
        {Array.from({ length: daysInMonth }).map((_, i) => {
          const day = i + 1;
          const key = formatDateKey(year, mo, day);
          const entry = map.get(key);
          const isToday = key === todayKey;
          const isSelected = key === selectedDay;

          const status = calorieStatus(entry?.calories ?? 0, goal.calories);
          const calMet = status === "ok";
          const proteinMet = proteinLevel(entry?.protein_g ?? 0, goal.protein_g) === 3;
          const bothMet = !!entry && calMet && proteinMet;
          const oneMet = !!entry && !bothMet && (calMet || proteinMet);

          const modifiers =
            (isToday ? " month-cal-cell--today" : "") +
            (isSelected ? " month-cal-cell--selected" : "") +
            (bothMet ? " month-cal-cell--both-met" : oneMet ? " month-cal-cell--one-met" : "");

          const achievementTitle = bothMet
            ? " — both goals hit"
            : oneMet
              ? ` — ${calMet ? "calories" : "protein"} goal hit`
              : "";

          return (
            <button
              key={key}
              type="button"
              className={`month-cal-cell${modifiers}`}
              aria-pressed={isSelected}
              onClick={() => onSelectDay(key)}
              title={`${key}${achievementTitle}`}
            >
              {(bothMet || oneMet) && (
                <span
                  className={`month-cal-cell__badge${bothMet ? " month-cal-cell__badge--full" : " month-cal-cell__badge--partial"}`}
                  aria-hidden="true"
                >
                  {bothMet ? "★★" : "★"}
                </span>
              )}
              <span className="month-cal-cell__day">{day}</span>
              {entry ? (
                <>
                  <span className={`month-cal-cell__cal month-cal-cell__cal--${status}`}>
                    {Math.round(entry.calories)}
                  </span>
                  <span
                    className={`month-cal-cell__pro${proteinMet ? " month-cal-cell__pro--met" : ""}`}
                  >
                    {Math.round(entry.protein_g * 10) / 10}g
                  </span>
                </>
              ) : (
                <span className="month-cal-cell__empty">–</span>
              )}
            </button>
          );
        })}
      </div>
      <div className="legend">
        <span className="legend-item">
          <span className="legend-swatch legend-swatch--cal-ok" /> Cal under goal
        </span>
        <span className="legend-item">
          <span className="legend-swatch legend-swatch--cal-warn" /> Cal near limit
        </span>
        <span className="legend-item">
          <span className="legend-swatch legend-swatch--cal-over" /> Cal over goal
        </span>
        <span className="legend-item">
          <span className="legend-swatch legend-swatch--pro-l3" /> Protein goal met
        </span>
        <span className="legend-item">
          <span className="legend-swatch legend-swatch--muted" /> Protein under goal
        </span>
        <span className="legend-item">
          <span className="month-cal-cell__badge month-cal-cell__badge--full legend-badge">★★</span> Both goals hit
        </span>
        <span className="legend-item">
          <span className="month-cal-cell__badge month-cal-cell__badge--partial legend-badge">★</span> One goal hit
        </span>
      </div>
    </div>
  );
}
